import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type JSX,
  type ReactNode,
} from "react";

export type AdminManualRefreshRegistration = {
  onRefresh: () => void;
  refreshing: boolean;
};

export type AdminManualRefreshSnapshot = {
  disabled: boolean;
};

const emptySnapshot: AdminManualRefreshSnapshot = {
  disabled: true,
};

type AdminManualRefreshEntry = {
  registration: AdminManualRefreshRegistration;
};

export function createAdminManualRefreshStore() {
  let entry: AdminManualRefreshEntry | null = null;
  let isRefreshPending = false;
  let snapshot = emptySnapshot;
  const listeners = new Set<() => void>();

  function publish(nextEntry: AdminManualRefreshEntry | null): void {
    entry = nextEntry;
    snapshot = nextEntry
      ? { disabled: nextEntry.registration.refreshing || isRefreshPending }
      : emptySnapshot;
    listeners.forEach((listener) => listener());
  }

  return {
    getSnapshot(): AdminManualRefreshSnapshot {
      return snapshot;
    },
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    register(nextRegistration: AdminManualRefreshRegistration): () => void {
      if (!nextRegistration.refreshing) {
        isRefreshPending = false;
      }
      const nextEntry = { registration: nextRegistration };
      publish(nextEntry);

      return () => {
        if (entry === nextEntry) {
          isRefreshPending = false;
          publish(null);
        }
      };
    },
    refresh(): void {
      if (!entry || entry.registration.refreshing || isRefreshPending) {
        return;
      }

      isRefreshPending = true;
      publish(entry);
      entry.registration.onRefresh();
    },
  };
}

export type AdminManualRefreshContextValue = {
  disabled: boolean;
  refresh: () => void;
  register: (registration: AdminManualRefreshRegistration) => () => void;
};

const unavailableAdminManualRefreshContext: AdminManualRefreshContextValue = {
  disabled: true,
  refresh: () => {},
  register: () => () => {},
};

const AdminManualRefreshContext = createContext<AdminManualRefreshContextValue>(
  unavailableAdminManualRefreshContext,
);

export function AdminManualRefreshProvider(props: {
  children: ReactNode;
}): JSX.Element {
  const storeRef = useRef<ReturnType<typeof createAdminManualRefreshStore> | null>(null);
  if (!storeRef.current) {
    storeRef.current = createAdminManualRefreshStore();
  }

  const store = storeRef.current;
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const refresh = useCallback(() => {
    store.refresh();
  }, [store]);
  const value = useMemo(
    () => ({
      disabled: snapshot.disabled,
      refresh,
      register: store.register,
    }),
    [refresh, snapshot.disabled, store.register],
  );

  return (
    <AdminManualRefreshContext.Provider value={value}>
      {props.children}
    </AdminManualRefreshContext.Provider>
  );
}

export function useAdminManualRefresh(): AdminManualRefreshContextValue {
  return useContext(AdminManualRefreshContext);
}

export function useAdminManualRefreshRegistration(
  registration: AdminManualRefreshRegistration,
): void {
  const { register } = useAdminManualRefresh();

  useEffect(() => register(registration), [register, registration]);
}
